import React, { useState, useEffect, useRef } from "react";
import AccessHistory from "./AccessHistory.jsx";
import CoordinatorAccess from "./CoordinatorAccess.jsx";

/**
 * AccessPanel — the one place that answers "who can see this profile?"
 *
 * Replaces AccessModal (contributors + share links, opened from a client list)
 * and CaregiverAccessModal (DSPs, opened from the editor hub). Four surfaces
 * answered one question, and the split ran along employment status — which is
 * our data model, not the coordinator's question. Someone asking "who can see
 * Maris?" had to open two of them and hold the answer in their head.
 *
 * THREE RULES THIS ENCODES (§4.2)
 *
 * 1. EVERY ROW CARRIES ITS REASON. "Why can he see this?" stops being a
 *    support conversation and becomes a glance. Only possible because the
 *    resolver can explain itself — `source`, `can`, `since` and `expires_at`
 *    all come from the server, computed by the same code that decides access.
 *
 * 2. INHERITED ACCESS IS A FACT, NOT A CONTROL. The admin and coordinators
 *    render greyed, with no Remove. Hiding them would make the list lie about
 *    who can see the profile; offering a Remove that cannot remove would be
 *    worse. This is the one deliberate exception to no-disabled-controls:
 *    showing something you can't change is fine, showing an ACTION you can't
 *    take is not.
 *
 * 3. LINKS ARE ROWS IN THE SAME LIST, visually distinct — dashed border, no
 *    avatar, label in quotation marks because it is the coordinator's note
 *    rather than a name. Filed in a separate dialog, a link is the thing you
 *    forget exists, which is how one outlives its reason.
 *
 * THE GROUPING IS THE SERVER'S. Each row arrives with `can` — build / add /
 * see — computed from capabilities_for(). A role-name table in here would be a
 * second copy of the permission model, and it would drift.
 */

const GROUPS = [
  { key: "build", title: "Can build this profile" },
  { key: "add",   title: "Can add to it" },
  { key: "see",   title: "Can see it" },
  // Here only to answer an interview they were sent (Oct 28). Wording is a
  // first draft for David.
  { key: "answer", title: "Answering an interview" },
];

const SOON_DAYS = 7;

function fmtDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleDateString(undefined,
    { month: "short", day: "numeric" });
}

function daysUntil(iso) {
  if (!iso) return null;
  return Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000);
}

/**
 * The reason line — one sentence per row saying how this person got here.
 *
 * "by Lucas" is deliberately absent: RoleAssignment records no granter, and
 * the audit table that does is write-only by design. Adding `granted_by` is a
 * migration that should ride with Track B's; until then the row says when,
 * which is the part that answers "is this still current?".
 */
function reasonFor(p) {
  if (p.source === "account") {
    return `${p.role_label} — has access to every profile`;
  }
  if (p.source === "selected") {
    return "Coordinator · chosen for this profile";
  }
  const bits = [p.role_label];
  if (p.source === "interview_link") {
    // Wording is a first draft for David (Oct 29).
    bits.push(p.open_count ? `opened ${p.open_count} time${p.open_count === 1 ? "" : "s"}` : "not opened yet");
    return bits.join(" · ");
  }
  if (p.since) bits.push(`since ${fmtDate(p.since)}`);
  return bits.join(" · ");
}

function PersonRow({ person, onRemove, onRenew, onCopy, copied, busy }) {
  const left = daysUntil(person.expires_at);
  const soon = left !== null && left <= SOON_DAYS;
  const inherited = person.source === "account";
  // A coordinator chosen in the Coordinators section (Oct 19): set there by
  // the admin, so no Remove here.
  const chosen = person.source === "selected";

  return (
    <div style={{ ...s.row, ...(inherited ? s.rowInherited : {}) }}>
      <div style={{ ...s.avatar, ...(inherited ? s.avatarInherited : {}) }}>
        {(person.display_name || "?")[0].toUpperCase()}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={s.name}>
          {person.display_name}
          {person.is_you && <span style={s.youTag}>you</span>}
        </div>
        <div style={s.reason}>{reasonFor(person)}</div>

        {/* Every time-bounded row states its end date. Within a week it turns
            amber and grows an action, because a date alone is information and
            what the manager needs at that point is a decision. */}
        {person.expires_at && (
          <div style={{ ...s.expiry, ...(soon ? s.expirySoon : {}) }}>
            {soon
              ? `Expires in ${left} day${left === 1 ? "" : "s"}`
              : `Expires ${fmtDate(person.expires_at)}`}
          </div>
        )}
      </div>

      {inherited ? (
        /* The fact, stated. No control, because there is no action to take
           here — their access is managed on the People page. */
        <span style={s.managedElsewhere}>Managed on the People page</span>
      ) : chosen ? (
        <span style={s.managedElsewhere}>Set under Coordinators</span>
      ) : (
        <div style={s.rowActions}>
          {person.source === "interview_link" ? (
            person.url && onCopy && (
              <button type="button" style={s.ghostBtn} onClick={() => onCopy(person)}>
                {copied ? "Copied" : "Copy link"}
              </button>
            )
          ) : person.expires_at && (
            <button type="button" style={s.ghostBtn} disabled={busy}
                    onClick={() => onRenew(person)}>
              Renew 30 days
            </button>
          )}
          {person.removable && (
            <button type="button" style={s.ghostBtn} disabled={busy}
                    onClick={() => onRemove(person)}>
              Remove
            </button>
          )}
        </div>
      )}
    </div>
  );
}

/** Someone invited who hasn't accepted. They cannot see the profile yet, and
 *  the row must never imply otherwise (§4.3). */
function PendingRow({ label, email, status, onCopy, onWithdraw, copied, busy }) {
  return (
    <div style={s.row}>
      <div style={{ ...s.avatar, ...s.avatarPending }}>⋯</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={s.name}>{label || email}</div>
        <div style={s.reason}>{status}</div>
      </div>
      <div style={s.rowActions}>
        {onCopy && (
          <button type="button" style={s.ghostBtn} onClick={onCopy}>
            {copied ? "Copied" : "Copy link"}
          </button>
        )}
        {onWithdraw && (
          <button type="button" style={s.ghostBtn} disabled={busy}
                  onClick={onWithdraw}>
            Remove
          </button>
        )}
      </div>
    </div>
  );
}

/** A share link, in the same list as the people — see rule 3. */
function LinkRow({ link, onCopy, onRevoke, copied, fresh = false }) {
  const left = daysUntil(link.expires_at);
  return (
    <div style={{ ...s.row, ...s.rowLink, ...(fresh ? s.rowFresh : {}) }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={s.name}>
          {link.label ? `"${link.label}"` : "Untitled link"}
        </div>
        <div style={s.reason}>
          Link · opened {link.access_count} time{link.access_count === 1 ? "" : "s"}
          {link.asks_reflection === false && " · no reflection"}
          {link.expires_at && ` · ${expiresWord(link.expires_at)}`}
          {left !== null && left <= SOON_DAYS && left >= 1 && " (soon)"}
        </div>
      </div>
      <div style={s.rowActions}>
        <button type="button" style={s.ghostBtn} onClick={onCopy}>
          {copied ? "Copied" : "Copy link"}
        </button>
        <button type="button" style={s.ghostBtn} onClick={onRevoke}>
          Revoke
        </button>
      </div>
    </div>
  );
}

/** "4h" → {expires_in_hours: 4}; "7d" → {expires_in_days: 7}. */
export function expiryBody(v) {
  const n = parseInt(v, 10);
  return v.endsWith("h") ? { expires_in_hours: n } : { expires_in_days: n };
}
const EXPIRY_OPTIONS = [["4h", "4 hours"], ["1d", "1 day"], ["7d", "7 days"], ["30d", "30 days"], ["90d", "90 days"]];

/** "expires in 4 h" / "expires tomorrow" for short links; a date otherwise. */
export function expiresWord(iso) {
  if (!iso) return "";
  const ms = new Date(iso).getTime() - Date.now();
  if (Number.isNaN(ms)) return "";
  if (ms <= 0) return "expired";
  const h = Math.round(ms / 3600000);
  if (h < 1)  return "expires within the hour";
  if (h < 36) return `expires in ${h} h`;
  return `expires ${fmtDate(iso)}`;
}

/**
 * Two homes (Oct 11): an overlay from the client lists (onClose given), and
 * inline inside the editor hub's "Who can see this" card (`inline`), beside
 * "Who's using this profile" — permission next to behaviour, separate cards.
 */
export default function AccessPanel({ client, onClose, inline = false }) {
  const [people, setPeople]   = useState(null);
  const [links, setLinks]     = useState(null);
  const [invites, setInvites] = useState(null);
  const [justMade, setJustMade] = useState(null);   // id of the link just created
  const [caregivers, setCaregivers] = useState(null);   // team + pending DSP invites
  const [roleOptions, setRoleOptions] = useState(null);
  const [isAgency, setIsAgency] = useState(false);

  const [query, setQuery]     = useState("");
  const [picked, setPicked]   = useState(null);   // a chosen candidate, or null
  const [role, setRole]       = useState(null);
  const [days, setDays]       = useState("ongoing");

  // Chunk 1's 7-day share-link default, carried across. AccessModal held it in
  // form state and always sent expires_in_days, so the server's
  // DEFAULT_EXPIRY_DAYS is never consulted from here — this line is the one a
  // person actually gets. Deleting that file without moving this would have
  // silently restored 30 days.
  const [linkForm, setLinkForm] = useState({ label: "", expiry: "7d", asksReflection: true });
  // Which of the two actions is open (Plan E4). They are different things —
  // an account vs. a bare link — and the panel now says so up front.
  const [action, setAction] = useState("give");

  const [busy, setBusy]       = useState(false);
  const [error, setError]     = useState(null);
  const [notice, setNotice]   = useState(null);
  const [copied, setCopied]   = useState(null);

  // "who can see this" and "who has been in here" are the same question in two
  // tenses, so they share a dialog rather than living in two places a
  // coordinator has to know about. Default to the present tense: that is the
  // question being asked when the panel is opened at all.
  const [view, setView] = useState("access");
  const queryRef = useRef(null);

  async function load() {
    try {
      const [p, l, i, c, rr] = await Promise.all([
        fetch(`/api/clients/${client.id}/access`),
        fetch(`/api/clients/${client.id}/share-links`),
        fetch(`/api/clients/${client.id}/invitations`),
        fetch(`/api/clients/${client.id}/caregivers`),
        fetch(`/api/clients/${client.id}/invitable-roles`),
      ]);
      if (!p.ok || !l.ok || !i.ok) throw new Error("We couldn't load this just now.");
      setPeople(await p.json());
      setLinks(await l.json());
      setInvites(await i.json());
      // A coordinator in a family account has no caregiver roster; a 403 here
      // is ordinary, not a failure worth showing.
      setCaregivers(c.ok ? await c.json() : { caregivers: [], pending: [] });
      if (rr.ok) {
        const d = await rr.json();
        setIsAgency(d.account_type !== "family");
        setRoleOptions(d.roles);
        if (d.roles?.length) setRole(r => r || d.roles[0].value);
      }
    } catch (e) { setError(e.message); }
  }

  useEffect(() => { load(); }, [client.id]);

  function say(message) { setNotice(message); setError(null); }

  // ── The Add flow ──────────────────────────────────────────────────────────
  //
  // ONE FIELD, AND THE USER NEVER CHOOSES A MECHANISM (§4.5).
  //
  // Type a name or an email. A match against someone already on the team is
  // assigned immediately; an open invitation gets this client queued onto it;
  // an unknown email creates an invitation. Lucas never learns which happened
  // — he typed a person, and the person has access.
  //
  // This is the gap the wireframe missed: its Add flow opened with "Their name
  // / Email address", which is an invitation form and therefore could not
  // assign Dawn to a fourth client at all.
  const listedIds = new Set((people || []).map(p => p.user_id));
  const candidates = [
    ...((caregivers?.caregivers || [])
        .filter(c => !listedIds.has(c.user_id))
        .map(c => ({ kind: "user", id: c.user_id,
                     label: c.display_name, sub: c.email }))),
    ...((caregivers?.pending || [])
        .filter(p => !p.assigned)
        .map(p => ({ kind: "invitation", id: p.invitation_id,
                     label: p.display_name, sub: `${p.email} · invited, not accepted` }))),
  ];

  const q = query.trim().toLowerCase();
  const matches = q
    ? candidates.filter(c =>
        (c.label || "").toLowerCase().includes(q) ||
        (c.sub || "").toLowerCase().includes(q)).slice(0, 6)
    : [];
  const looksLikeEmail = q.includes("@") && q.includes(".");
  const showInviteOption = looksLikeEmail && matches.length === 0;

  async function give() {
    setBusy(true); setError(null); setNotice(null);
    try {
      if (picked?.kind === "user") {
        const body = { user_id: picked.id };
        if (days !== "ongoing") body.expires_in_days = Number(days);
        const r = await fetch(`/api/clients/${client.id}/caregivers`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Couldn't give them access.");
        say(`${picked.label} now has access.`);

      } else if (picked?.kind === "invitation") {
        const r = await fetch(`/api/clients/${client.id}/caregivers`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ invitation_id: picked.id }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Couldn't queue that.");
        say(`${picked.label} will get access when they join.`);

      } else {
        const r = await fetch(`/api/clients/${client.id}/invitations`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: q, role: isAgency ? role : "family_contributor" }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || "Couldn't create that invitation.");
        say(d.already_member ? d.message
                             : `Invitation ready for ${d.email}. Copy the link below.`);
      }
      setQuery(""); setPicked(null); setDays("ongoing");
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  // ── Expiry ────────────────────────────────────────────────────────────────
  //
  // The button names its own amount rather than promising "the original
  // duration". Only expires_at is stored, so after one renewal the original
  // span is no longer recoverable — a button that silently extended by a
  // different amount each time would be worse than one that says what it does.
  async function renew(person) {
    setBusy(true); setError(null);
    try {
      const when = new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 19);
      const r = await fetch(
        `/api/clients/${client.id}/caregivers/${person.user_id}`, {
          method: "PATCH", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ expires_at: when }),
        });
      if (!r.ok) throw new Error((await r.json()).error || "Couldn't renew that.");
      say(`${person.display_name}'s access now runs to ${fmtDate(when)}.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  // ── Removal (§4.6) ────────────────────────────────────────────────────────
  //
  // The confirmation carries the model's guarantee, at the exact moment
  // someone is worried about it: contributions stay, and this is reversible.
  async function remove(person) {
    if (person.link_id) return removeLink(person);
    if (!window.confirm(
      `Remove ${person.display_name}'s access to ${client.display_name}?\n\n` +
      `They'll no longer be able to open this profile. Photos and notes they ` +
      `added stay, and you can give them access again at any time. An ` +
      `interview sent to them that they haven't started is taken back.`)) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(
        `/api/clients/${client.id}/access/${person.user_id}`, { method: "DELETE" });
      if (!r.ok) throw new Error((await r.json()).error || "Couldn't remove access.");
      say(`${person.display_name} no longer has access.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function removeLink(person) {
    if (!window.confirm(
      `Remove ${person.display_name}'s interview link?\n\n` +
      `The link stops working at once. If they haven't started, the interview ` +
      `goes back to your drafts; anything they've answered stays.`)) return;
    setBusy(true); setError(null);
    try {
      const r = await fetch(
        `/api/clients/${client.id}/interview-links/${person.link_id}`, { method: "DELETE" });
      if (!r.ok) throw new Error((await r.json()).error || "Couldn't remove that link.");
      say(`${person.display_name}'s link no longer works.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function unqueue(p) {
    if (!window.confirm(
      `${p.display_name} is invited but hasn't joined yet. Take ` +
      `${client.display_name} off their list?`)) return;
    try {
      const r = await fetch(
        `/api/clients/${client.id}/caregivers/${p.invitation_id}?type=invitation`,
        { method: "DELETE" });
      if (!r.ok) throw new Error("Couldn't change that.");
      await load();
    } catch (e) { setError(e.message); }
  }

  async function withdraw(inv) {
    if (!window.confirm(`Withdraw the invitation to ${inv.email}? The link stops working.`)) return;
    try {
      const r = await fetch(`/api/invitations/${inv.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error((await r.json()).error || "Couldn't withdraw that.");
      await load();
    } catch (e) { setError(e.message); }
  }

  async function makeLink() {
    setBusy(true); setError(null);
    try {
      const r = await fetch(`/api/clients/${client.id}/share-links`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          label: linkForm.label.trim() || null,
          asks_reflection: linkForm.asksReflection,
          ...expiryBody(linkForm.expiry),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || "Couldn't create that link.");
      setLinkForm(f => ({ ...f, label: "" }));
      // The link is on the clipboard the moment it exists; the button says
      // so, and the new row lights up in the list above so "where did it
      // go" never arises (C8).
      copy("link", d);
      setJustMade(d.id);
      setTimeout(() => setJustMade(null), 4000);
      say("Link created and copied — paste it wherever you're sending it.");
      await load();
    } catch (e) { setError(e.message); }
    finally { setBusy(false); }
  }

  async function revokeLink(link) {
    if (!window.confirm(
      `Turn off this link${link.label ? ` for "${link.label}"` : ""}? ` +
      `Anyone using it loses access straight away.`)) return;
    try {
      const r = await fetch(`/api/share-links/${link.id}`, { method: "DELETE" });
      if (!r.ok) throw new Error("Couldn't turn off that link.");
      await load();
    } catch (e) { setError(e.message); }
  }

  function copy(kind, obj) {
    const path = kind === "link" ? `/share/${obj.token}`
               : kind === "interview" ? obj.url
               : `/invite/${obj.token}`;
    navigator.clipboard?.writeText(`${window.location.origin}${path}`);
    setCopied(kind === "interview" ? obj.link_id : obj.id);
    setTimeout(() => setCopied(null), 2000);
  }

  // ── Assembling the one list ───────────────────────────────────────────────
  const activeLinks = (links || []).filter(l => l.is_active);
  const pendingInv  = (invites || []).filter(i => i.status === "pending");
  const queuedDsps  = (caregivers?.pending || []).filter(p => p.assigned);
  const loading     = people === null || links === null || invites === null;

  const byGroup = k => (people || []).filter(p => p.can === k);
  const total = (people || []).length + activeLinks.length
              + pendingInv.length + queuedDsps.length;

  return (
    <div style={inline ? s.inlineWrap : s.overlay} onKeyDown={e => { if (!inline && e.key === "Escape") onClose?.(); }}>
      <div style={inline ? s.inlineBox : s.box}>
        {!inline && (
          <div style={s.header}>
            <div>
              <div style={s.title}>Who can see {client.display_name}</div>
              {!loading && <div style={s.subtitle}>{total} with access</div>}
            </div>
            <button style={s.close} onClick={onClose} aria-label="Close">×</button>
          </div>
        )}
        {inline && !loading && <div style={s.subtitle}>{total} with access</div>}

        <div style={s.viewTabs} role="tablist">
          {[["access", "Who has access"], ["history", "Recent activity"]].map(([k, label]) => (
            <button key={k} type="button" role="tab" aria-selected={view === k}
                    style={{ ...s.viewTab, ...(view === k ? s.viewTabOn : {}) }}
                    onClick={() => setView(k)}>
              {label}
            </button>
          ))}
        </div>

        <div style={s.scroll}>
          {view === "history" && <AccessHistory client={client} />}
          {view === "access" && <>
          {error  && <div style={s.error}>{error}</div>}
          {notice && <div style={s.notice}>{notice}</div>}
          {loading && <p style={s.muted}>Loading…</p>}

          {/* ── What do you want to do? (Plan E4) ─────────────────────── */}
          <div style={s.chooser} role="tablist" aria-label="Action">
            {[
              ["give", "Give someone access", "They sign in with their own account and can add to the profile. Staff and family."],
              ["link", "Share a read-only link", "No account. Anyone with the link can read it until it expires. Respite, a substitute, a doctor."],
            ].map(([k, title, sub]) => (
              <button key={k} type="button" role="tab" aria-selected={action === k}
                      style={{ ...s.choice, ...(action === k ? s.choiceOn : {}) }}
                      onClick={() => setAction(k)}>
                <span style={s.choiceTitle}>{title}</span>
                <span style={s.choiceSub}>{sub}</span>
              </button>
            ))}
          </div>

          {/* ── Give someone access ───────────────────────────────────── */}
          {action === "give" && <div style={s.actionForm}>

            <input
              ref={queryRef}
              style={s.input}
              disabled={busy}
              placeholder="Type a name or email address"
              value={picked ? picked.label : query}
              onChange={e => { setPicked(null); setQuery(e.target.value); }}
            />

            {!picked && matches.length > 0 && (
              <div style={s.matchList}>
                {matches.map(c => (
                  <button key={`${c.kind}-${c.id}`} type="button" style={s.match}
                          onClick={() => setPicked(c)}>
                    <span style={s.matchName}>{c.label}</span>
                    <span style={s.matchSub}>{c.sub}</span>
                  </button>
                ))}
              </div>
            )}

            {/* The role question belongs to the invitation path only. Someone
                already on the team IS a DSP — asking what an existing person
                should "be able to do" would be asking about their job. */}
            {!picked && showInviteOption && isAgency && roleOptions && (
              <>
                <div style={s.fieldLabel}>How do they know {client.display_name}?</div>
                <div style={s.chips}>
                  {roleOptions.map(r => (
                    <button key={r.value} type="button"
                            style={{ ...s.chip, ...(role === r.value ? s.chipOn : {}) }}
                            onClick={() => setRole(r.value)}>
                      {r.label}
                    </button>
                  ))}
                </div>
                <div style={s.hint}>
                  This doesn't change what they can do — everyone invited here can
                  read the profile and add to it.
                </div>
              </>
            )}

            {/* Optional time-bound, offered only where it can apply: an
                assignment. Ongoing is the default, because most assignments
                are, and a default expiry with no renewal habit is how people
                silently lose access. */}
            {picked?.kind === "user" && (
              <>
                <div style={s.fieldLabel}>How long?</div>
                <div style={s.chips}>
                  {[["ongoing", "Ongoing"], ["7", "7 days"], ["30", "30 days"]].map(
                    ([v, label]) => (
                      <button key={v} type="button"
                              style={{ ...s.chip, ...(days === v ? s.chipOn : {}) }}
                              onClick={() => setDays(v)}>
                        {label}
                      </button>
                    ))}
                </div>
              </>
            )}

            <button style={{ ...s.primaryBtn, ...(busy ? s.disabled : {}) }}
                    disabled={busy || (!picked && !showInviteOption)}
                    onClick={give}>
              {busy ? "Working…"
                    : picked ? `Give ${picked.label} access`
                    : "Send an invitation"}
            </button>

            {!picked && q && !showInviteOption && matches.length === 0 && (
              <div style={s.hint}>
                No one by that name. Type a full email address to invite someone new.
              </div>
            )}
          </div>}

          {/* ── Share a link ──────────────────────────────────────────── */}
          {action === "link" && <div style={s.actionForm}>
            <div style={s.formRow}>
              <input style={{ ...s.input, flex: 2 }} disabled={busy}
                     placeholder="Who is this for? (e.g. Amy, Tuesday respite)"
                     value={linkForm.label}
                     onChange={e => setLinkForm(f => ({ ...f, label: e.target.value }))} />
              <select style={{ ...s.input, flex: 1 }} disabled={busy}
                      value={linkForm.expiry} aria-label="Expires in"
                      onChange={e => setLinkForm(f => ({ ...f, expiry: e.target.value }))}>
                {EXPIRY_OPTIONS.map(([v, label]) => <option key={v} value={v}>Expires in {label}</option>)}
              </select>
            </div>
            {/* Per link (David, Oct 15): a link for feedback asks "one thing
                you'll remember" at the end; a link for a quiet look-around
                doesn't. Visitors' answers show up as "Visitor via link". */}
            <label style={s.checkRow}>
              <input type="checkbox" checked={linkForm.asksReflection} disabled={busy}
                     onChange={e => setLinkForm(f => ({ ...f, asksReflection: e.target.checked }))} />
              <span>Ask visitors for one thing they'll remember, at the end</span>
            </label>
            <button style={{ ...s.secondaryBtn, ...(busy ? s.disabled : {}) }}
                    disabled={busy} onClick={makeLink}>
              {justMade ? "Copied ✓" : "Create a link"}
            </button>
            {/* The link is forwardable by design; the guardrail is a habit,
                not a control, and saying so plainly is the honest version. */}
            <div style={s.hint}>
              Anyone with this link can read the profile. Only share it with
              someone who has a reason to see it.
            </div>
          </div>}

          {/* Which coordinators (Oct 19). Agency accounts only; it renders
              nothing for a household. */}
          {isAgency && (
            <CoordinatorAccess clientId={client.id} clientName={client.display_name}
                               onChanged={load} />
          )}

          <div style={s.listHead}>Who has access now</div>
          {!loading && GROUPS.map(g => {
            const rows = byGroup(g.key);
            const extras = g.key === "add" ? pendingInv.filter(i => !i.interview_only)
                         : g.key === "answer" ? pendingInv.filter(i => i.interview_only) : [];
            const links_ = g.key === "see" ? activeLinks : [];
            const queued = g.key === "see" ? queuedDsps : [];
            if (!rows.length && !extras.length && !links_.length && !queued.length) {
              return null;
            }
            return (
              <div key={g.key} style={s.group}>
                <div style={s.groupTitle}>{g.title}</div>

                {rows.map(p => (
                  <PersonRow key={p.user_id || p.link_id} person={p} busy={busy}
                             onRemove={remove} onRenew={renew}
                             copied={copied === p.link_id}
                             onCopy={() => copy("interview", p)} />
                ))}

                {/* Invited contributors, not yet accepted. */}
                {extras.map(inv => (
                  <PendingRow key={inv.id}
                              label={inv.name || inv.email} email={inv.email}
                              status={inv.interview_only
                                ? `Invited ${fmtDate(inv.created_at)} to answer an interview · not accepted`
                                : `Invited ${fmtDate(inv.created_at)} · not accepted`}
                              copied={copied === inv.id}
                              onCopy={() => copy("invite", inv)}
                              onWithdraw={() => withdraw(inv)} />
                ))}

                {/* Invited DSPs queued onto this client (§4.3). They have no
                    User row yet, so they are not in `people` — and the row
                    must never imply they can already open the profile. */}
                {queued.map(p => (
                  <PendingRow key={p.invitation_id}
                              label={p.display_name} email={p.email}
                              status="Invited, not accepted · will get access when they join"
                              busy={busy}
                              onWithdraw={() => unqueue(p)} />
                ))}

                {links_.map(l => (
                  <LinkRow key={l.id} link={l} copied={copied === l.id} fresh={justMade === l.id}
                           onCopy={() => copy("link", l)}
                           onRevoke={() => revokeLink(l)} />
                ))}
              </div>
            );
          })}

          </>}
        </div>
      </div>
    </div>
  );
}

const s = {
  viewTabs:  { display: "flex", gap: 4, padding: "var(--space-3) var(--space-6) 0" },
  viewTab:   { fontSize: "var(--text-sm)", padding: "6px 12px", border: "none", borderBottom: "2px solid transparent", background: "none", color: "var(--color-text-tertiary)", cursor: "pointer" },
  viewTabOn: { color: "var(--color-text-primary)", borderBottom: "2px solid var(--color-accent)", fontWeight: "var(--weight-medium)" },
  overlay:   { position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 100, padding: "var(--space-4)" },
  box:       { background: "var(--color-surface)", borderRadius: "var(--radius-xl)", width: 560, maxWidth: "100%", maxHeight: "86vh", display: "flex", flexDirection: "column" },
  inlineWrap:{ display: "block" },
  inlineBox: { display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  header:    { display: "flex", alignItems: "flex-start", justifyContent: "space-between", padding: "var(--space-6) var(--space-6) var(--space-4)", borderBottom: "1px solid var(--color-border)" },
  title:     { fontSize: "var(--text-lg)", fontWeight: "var(--weight-semibold)", color: "var(--color-text-primary)" },
  subtitle:  { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 2 },
  close:     { background: "none", border: "none", fontSize: 22, color: "var(--color-text-tertiary)", cursor: "pointer", lineHeight: 1, padding: 0 },
  scroll:    { overflowY: "auto", padding: "var(--space-5) var(--space-6) var(--space-6)", display: "flex", flexDirection: "column", gap: "var(--space-5)" },

  group:      { display: "flex", flexDirection: "column" },
  groupTitle: { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-tertiary)", marginBottom: 8 },

  row:          { display: "flex", alignItems: "center", gap: 12, padding: "10px 0", borderBottom: "1px solid var(--color-border)" },
  rowInherited: { opacity: 0.6 },
  rowLink:      { border: "1px dashed var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 12px", marginTop: 8, borderBottom: "1px dashed var(--color-border)", transition: "background 600ms ease, border-color 600ms ease" },
  rowFresh:     { background: "var(--color-accent-subtle)", border: "1px solid var(--color-accent)" },
  avatar:          { width: 34, height: 34, borderRadius: "50%", background: "var(--color-accent-subtle)", color: "var(--color-accent)", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: "var(--weight-medium)", fontSize: 14, flexShrink: 0 },
  avatarInherited: { background: "var(--color-bg-inset)", color: "var(--color-text-tertiary)" },
  avatarPending:   { background: "var(--color-bg-inset)", color: "var(--color-text-tertiary)", fontSize: 16 },
  name:    { fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", color: "var(--color-text-primary)", display: "flex", alignItems: "center", gap: 6 },
  youTag:  { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.06em", color: "var(--color-text-tertiary)", border: "1px solid var(--color-border)", borderRadius: 999, padding: "1px 6px" },
  reason:  { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 2 },
  checkRow:    { display: "flex", alignItems: "center", gap: "var(--space-2)", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", margin: "var(--space-2) 0" },
  expiry:      { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", marginTop: 3 },
  expirySoon:  { color: "var(--color-warning, #b45309)", fontWeight: "var(--weight-medium)" },
  managedElsewhere: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)", flexShrink: 0 },
  rowActions:  { display: "flex", gap: 6, flexShrink: 0 },
  ghostBtn:    { font: "inherit", fontSize: "var(--text-xs)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "4px 10px", color: "var(--color-text-secondary)", cursor: "pointer" },

  chooser:    { display: "grid", gridTemplateColumns: "1fr 1fr", gap: "var(--space-2)" },
  choice:     { font: "inherit", textAlign: "left", display: "flex", flexDirection: "column", gap: 4, padding: "var(--space-3)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-lg)", background: "var(--color-surface)", cursor: "pointer", color: "var(--color-text-secondary)" },
  choiceOn:   { border: "1px solid var(--color-accent)", background: "var(--color-accent-subtle)", color: "var(--color-text-primary)" },
  choiceTitle:{ fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)" },
  choiceSub:  { fontSize: 11, lineHeight: 1.4, color: "var(--color-text-tertiary)" },
  actionForm: { display: "flex", flexDirection: "column", gap: "var(--space-3)", padding: "var(--space-3) 0 var(--space-4)", borderBottom: "1px solid var(--color-border)" },
  listHead:   { fontSize: 10, fontWeight: "var(--weight-semibold)", textTransform: "uppercase", letterSpacing: "0.07em", color: "var(--color-text-tertiary)", paddingTop: "var(--space-4)" },
  addBlock:   { display: "flex", flexDirection: "column", gap: "var(--space-3)", paddingTop: "var(--space-4)", borderTop: "1px solid var(--color-border)" },
  formRow:    { display: "flex", gap: "var(--space-2)" },
  input:      { font: "inherit", fontSize: "var(--text-sm)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)", color: "var(--color-text-primary)", background: "var(--color-bg-base)", width: "100%", boxSizing: "border-box" },
  matchList:  { display: "flex", flexDirection: "column", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", overflow: "hidden" },
  match:      { font: "inherit", textAlign: "left", background: "none", border: "none", borderBottom: "1px solid var(--color-border)", padding: "8px 12px", cursor: "pointer", display: "flex", flexDirection: "column" },
  matchName:  { fontSize: "var(--text-sm)", color: "var(--color-text-primary)" },
  matchSub:   { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  fieldLabel: { fontSize: "var(--text-xs)", fontWeight: "var(--weight-medium)", color: "var(--color-text-secondary)" },
  chips:      { display: "flex", gap: 6, flexWrap: "wrap" },
  chip:       { font: "inherit", fontSize: "var(--text-xs)", background: "none", border: "1px solid var(--color-border)", borderRadius: 999, padding: "5px 12px", color: "var(--color-text-secondary)", cursor: "pointer" },
  chipOn:     { border: "1px solid var(--color-accent)", background: "var(--color-accent-subtle)", color: "var(--color-text-primary)" },
  hint:       { fontSize: 11, color: "var(--color-text-tertiary)", lineHeight: 1.5 },

  primaryBtn:   { font: "inherit", fontSize: "var(--text-sm)", fontWeight: "var(--weight-medium)", background: "var(--color-accent)", color: "var(--color-text-on-accent)", border: "none", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", cursor: "pointer" },
  secondaryBtn: { font: "inherit", fontSize: "var(--text-sm)", background: "none", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-2) var(--space-5)", color: "var(--color-text-secondary)", cursor: "pointer", alignSelf: "flex-start" },
  disabled:     { opacity: 0.5, cursor: "not-allowed" },

  error:  { fontSize: "var(--text-sm)", color: "var(--color-critical)", background: "var(--color-critical-bg)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  notice: { fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", background: "var(--color-bg-inset)", borderRadius: "var(--radius-sm)", padding: "var(--space-2) var(--space-3)" },
  muted:  { fontSize: "var(--text-sm)", color: "var(--color-text-tertiary)" },
};
